import { computed, inject, Injectable, signal } from '@angular/core';
import { SignalRService } from '@app/core/services/signalr.service';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { catchError, debounceTime, forkJoin, of, Subject, Subscription, switchMap, tap } from 'rxjs';
import { ChatMessagePush, OneOnOneChatDelivery, OneOnOneChatMessage } from '../model/one-on-one-chat.types';
import { OneOnOneChatHttpService } from '../services/one-on-one-chat-http.service';
import {
  findFirstUnread,
  groupByDay,
  mapMessageDto,
  mapMessagePush,
  mergeMessages,
} from '../utils/one-on-one-chat-messages.util';

const CHAT_MESSAGE_EVENT = 'chatMessage';
const MARK_AS_READ_DEBOUNCE_MS = 1000;

@Injectable({ providedIn: 'root' })
export class OneOnOneChatSandbox {
  private readonly httpService = inject(OneOnOneChatHttpService);
  private readonly signalRService = inject(SignalRService);

  readonly messages = signal<OneOnOneChatMessage[]>([]);
  readonly days = computed(() => groupByDay(this.messages()));
  readonly loading = signal(false);
  readonly error = signal(false);
  readonly loadingOlder = signal(false);
  readonly firstUnreadMessageId = signal<string | null>(null);

  private readonly continuationToken = signal<string | null>(null);
  readonly hasOlder = computed(() => this.continuationToken() !== null);

  private readonly readRequests = new Subject<void>();

  private conversationId: string | null = null;
  private lastReadMessageId: string | null = null;
  private subscription: Subscription | null = null;
  private olderSubscription: Subscription | null = null;
  private catchUpSubscription: Subscription | null = null;
  private resumeListener: Promise<PluginListenerHandle> | null = null;

  constructor() {
    this.readRequests.pipe(debounceTime(MARK_AS_READ_DEBOUNCE_MS)).subscribe(() => this.markNewestAsRead());
  }

  load(agentExternalId: string): void {
    this.reset();
    this.loading.set(true);
    this.signalRService.on<ChatMessagePush>(CHAT_MESSAGE_EVENT, (push) => this.receive(push));
    this.resumeListener = App.addListener('resume', () => this.catchUp());

    this.subscription = this.httpService
      .createConversation(agentExternalId)
      .pipe(
        tap(({ conversationId }) => (this.conversationId = conversationId)),
        switchMap(({ conversationId }) =>
          forkJoin({
            page: this.httpService.getMessages(conversationId),
            conversation: this.httpService.getConversation(conversationId).pipe(catchError(() => of(null))),
          }),
        ),
      )
      .subscribe({
        next: ({ page, conversation }) => {
          // Merged rather than set: pushes may already have arrived while the page was loading.
          this.messages.update((messages) => mergeMessages(messages, page.messages.map(mapMessageDto)));
          this.lastReadMessageId = conversation?.readState?.lastReadMessageId ?? null;
          this.continuationToken.set(page.continuationToken);
          this.firstUnreadMessageId.set(conversation ? findFirstUnread(this.messages(), this.lastReadMessageId) : null);
          this.loading.set(false);
          this.markNewestAsRead();
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  loadOlder(done: () => void): void {
    const token = this.continuationToken();
    if (!this.conversationId || !token || this.loadingOlder()) {
      done();
      return;
    }
    this.loadingOlder.set(true);

    this.olderSubscription = this.httpService.getMessages(this.conversationId, token).subscribe({
      next: (page) => {
        done();
        this.messages.update((messages) => mergeMessages(messages, page.messages.map(mapMessageDto)));
        this.continuationToken.set(page.continuationToken);
        this.loadingOlder.set(false);
      },
      error: () => {
        done();
        this.loadingOlder.set(false);
      },
    });
  }

  /* Optimistic: the message shows straight away and only its delivery state follows the request. */
  send(text: string): void {
    const message = text.trim();
    if (!message || !this.conversationId) return;

    const clientReferenceId = crypto.randomUUID();
    const local: OneOnOneChatMessage = {
      id: `local:${clientReferenceId}`,
      isOwn: true,
      text: message,
      createdOn: new Date(),
      delivery: OneOnOneChatDelivery.Sending,
      clientReferenceId,
    };
    this.messages.update((messages) => [...messages, local]);
    this.post(local);
  }

  /* Re-posts with the same clientReferenceId, which the server dedupes on. */
  retry(message: OneOnOneChatMessage): void {
    if (message.delivery !== OneOnOneChatDelivery.Failed) return;

    this.updateLocal(message.clientReferenceId, { delivery: OneOnOneChatDelivery.Sending });
    this.post(message);
  }

  reset(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.olderSubscription?.unsubscribe();
    this.olderSubscription = null;
    this.catchUpSubscription?.unsubscribe();
    this.catchUpSubscription = null;
    void this.resumeListener?.then((listener) => listener.remove());
    this.resumeListener = null;
    this.conversationId = null;
    this.lastReadMessageId = null;
    this.continuationToken.set(null);
    this.messages.set([]);
    this.loading.set(false);
    this.loadingOlder.set(false);
    this.firstUnreadMessageId.set(null);
    this.error.set(false);
  }

  private receive(push: ChatMessagePush): void {
    if (!this.conversationId || push.conversationId !== this.conversationId) return;

    this.messages.update((messages) => mergeMessages(messages, [mapMessagePush(push)]));
    if (!this.loading()) this.readRequests.next();
  }

  // The hub is stopped while the app is in the background, so pushes sent in the meantime are lost.
  private catchUp(): void {
    if (!this.conversationId || this.loading() || this.error()) return;

    this.catchUpSubscription?.unsubscribe();
    this.catchUpSubscription = this.httpService.getMessages(this.conversationId).subscribe({
      next: (page) => {
        this.messages.update((messages) => mergeMessages(messages, page.messages.map(mapMessageDto)));
        this.readRequests.next();
      },
      error: () => undefined,
    });
  }

  private markNewestAsRead(): void {
    const newest = this.messages()
      .filter((message) => message.delivery === OneOnOneChatDelivery.Sent)
      .at(-1);
    if (!this.conversationId || !newest || newest.id === this.lastReadMessageId) return;

    this.lastReadMessageId = newest.id;
    this.httpService.markAsRead(this.conversationId, newest.id).subscribe({ error: () => undefined });
  }

  /* Deliberately not cancelled on reset: a message the candidate sent should still arrive after the
     modal closes. Its late update then finds no match in the cleared list and does nothing. */
  private post(message: OneOnOneChatMessage): void {
    const { clientReferenceId, text } = message;
    this.httpService.sendMessage(this.conversationId, { message: text, clientReferenceId }).subscribe({
      next: ({ messageId }) => this.confirmLocal(clientReferenceId, messageId),
      error: () => this.updateLocal(clientReferenceId, { delivery: OneOnOneChatDelivery.Failed }),
    });
  }

  // A catch-up can bring in the stored message before its POST returns; keep that one, drop the copy.
  private confirmLocal(clientReferenceId: string, messageId: string): void {
    if (this.messages().some((message) => message.id === messageId)) {
      this.messages.update((messages) => messages.filter((message) => message.clientReferenceId !== clientReferenceId));
      return;
    }
    this.updateLocal(clientReferenceId, { id: messageId, delivery: OneOnOneChatDelivery.Sent });
  }

  private updateLocal(clientReferenceId: string, changes: Partial<OneOnOneChatMessage>): void {
    this.messages.update((messages) =>
      messages.map((message) =>
        message.clientReferenceId === clientReferenceId ? { ...message, ...changes } : message,
      ),
    );
  }
}
